import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  AccountBalanceWallet,
  Payments,
  ReceiptLong,
  MonetizationOn,
  Block,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  ErrorOutline,
  Info,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  procurementService,
  PaymentProposal,
  PaymentProposalInvoice,
  PaymentRunResult,
} from '../../services/procurementService';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const todayIso = (): string => new Date().toISOString().split('T')[0];

const matchStatusTone: Record<string, StatusTone> = {
  UNMATCHED: 'neutral',
  MATCHED: 'success',
  WITHIN_TOLERANCE: 'warning',
  EXCEPTION: 'error',
  OVERRIDDEN: 'info',
};

/** How urgently an invoice is due — never colour alone, the label carries it. */
const dueTone = (daysToDue: number): StatusTone => {
  if (daysToDue < 0) return 'error';
  if (daysToDue <= 3) return 'warning';
  return 'neutral';
};

const dueLabel = (daysToDue: number): string => {
  if (daysToDue < 0) return `Overdue ${Math.abs(daysToDue)}d`;
  if (daysToDue === 0) return 'Due today';
  return `Due in ${daysToDue}d`;
};

const PAYMENT_MODES: { value: 'NEFT' | 'RTGS' | 'CHEQUE'; label: string }[] = [
  { value: 'NEFT', label: 'NEFT' },
  { value: 'RTGS', label: 'RTGS' },
  { value: 'CHEQUE', label: 'Cheque' },
];

// ---------------------------------------------------------------------------
// Confirmation dialog
// ---------------------------------------------------------------------------

const ConfirmRunDialog: React.FC<{
  open: boolean;
  count: number;
  net: number;
  bankLabel: string;
  paymentMode: string;
  paymentDate: string;
  submitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ open, count, net, bankLabel, paymentMode, paymentDate, submitting, onConfirm, onClose }) => (
  <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Payments color="primary" />
      Create Payment Run
    </DialogTitle>
    <DialogContent>
      <Alert severity="info" sx={{ mb: 2.5 }}>
        This releases payment against every selected invoice. It cannot be undone from here.
      </Alert>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Invoices selected</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{count}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Net payable</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {formatIndianNumber(net)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Payment date</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtDate(paymentDate)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Mode</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{paymentMode}</Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">From account</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', maxWidth: 220 }}>{bankLabel}</Typography>
        </Box>
      </Box>
    </DialogContent>
    <DialogActions sx={{ p: 2.5 }}>
      <Button onClick={onClose} disabled={submitting}>Cancel</Button>
      <Button variant="contained" onClick={onConfirm} disabled={submitting} startIcon={submitting ? <CircularProgress size={16} /> : <PlayArrow />}>
        {submitting ? 'Running…' : 'Confirm & Pay'}
      </Button>
    </DialogActions>
  </Dialog>
);

// ---------------------------------------------------------------------------
// Result panel
// ---------------------------------------------------------------------------

const ResultPanel: React.FC<{ result: PaymentRunResult; onDismiss: () => void }> = ({ result, onDismiss }) => {
  const navigate = useNavigate();
  return (
  <TableSurface>
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Payment Run Complete
          </Typography>
        </Box>
        <Button size="small" onClick={onDismiss}>Dismiss</Button>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {result.totals.count} payment{result.totals.count === 1 ? '' : 's'} created — {formatIndianNumber(result.totals.net)} net
        {result.skipped.length > 0 && ` · ${result.skipped.length} skipped`}
      </Typography>

      {result.created.length > 0 && (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Payment #</TableCell>
                <TableCell sx={headerCellSx}>Vendor</TableCell>
                <TableCell sx={headerCellSx}>Invoice</TableCell>
                <TableCell sx={headerCellSx} align="right">Gross</TableCell>
                <TableCell sx={headerCellSx} align="right">TDS</TableCell>
                <TableCell sx={headerCellSx} align="right">Net</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.created.map((c) => (
                <TableRow
                  key={c.invoiceId}
                  hover
                  sx={{ ...bodyRowSx, cursor: 'pointer' }}
                  onClick={() => navigate(`/procurement/payments/${c.paymentId}`)}
                >
                  <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{c.paymentNumber}</TableCell>
                  <TableCell>{c.vendorName}</TableCell>
                  <TableCell>{c.invoiceNumber}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(c.gross)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(c.tds)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatIndianNumber(c.net)}</TableCell>
                  <TableCell><StatusDot label={c.status} tone="success" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {result.skipped.length > 0 && (
        <Alert severity="warning" icon={<ErrorOutline />}>
          <AlertTitle>{result.skipped.length} invoice{result.skipped.length === 1 ? '' : 's'} skipped</AlertTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
            {result.skipped.map((s) => (
              <Typography key={s.invoiceId} variant="caption">
                Invoice #{s.invoiceId} — {s.reason}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}
    </Box>
  </TableSurface>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const PaymentProposalPage: React.FC = () => {
  const C = useControlRoomColors();

  const [horizonDays, setHorizonDays] = useState<number>(7);
  const [proposal, setProposal] = useState<PaymentProposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bankAccountId, setBankAccountId] = useState<string>('');
  const [paymentMode, setPaymentMode] = useState<'NEFT' | 'RTGS' | 'CHEQUE'>('NEFT');
  const [paymentDate, setPaymentDate] = useState<string>(todayIso());

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<PaymentRunResult | null>(null);
  const [blockedOpen, setBlockedOpen] = useState(false);

  const fetchProposal = useCallback((days: number) => {
    setLoading(true);
    setError(null);
    procurementService
      .getPaymentProposal(days)
      .then((data) => {
        setProposal(data);
        setSelectedIds(new Set());
        // Default the bank account once, or if the current selection no
        // longer exists in the refreshed list.
        setBankAccountId((prev) => {
          const stillValid = prev && data.bankAccounts.some((b) => String(b.id) === prev);
          if (stillValid) return prev;
          return data.defaultBankAccount != null ? String(data.defaultBankAccount) : '';
        });
      })
      .catch((err: any) => setError(err.message || 'Failed to load payment proposal'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchProposal(horizonDays);
  }, [horizonDays, fetchProposal]);

  const invoices = useMemo<PaymentProposalInvoice[]>(() => {
    const list = proposal?.invoices || [];
    return [...list].sort((a, b) => a.daysToDue - b.daysToDue);
  }, [proposal]);

  const allSelected = invoices.length > 0 && selectedIds.size === invoices.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(invoices.map((inv) => inv.invoiceId)));
  };

  const selectedTotals = useMemo(() => {
    const rows = invoices.filter((inv) => selectedIds.has(inv.invoiceId));
    return rows.reduce(
      (acc, inv) => ({ gross: acc.gross + inv.gross, tds: acc.tds + inv.tds, net: acc.net + inv.net }),
      { gross: 0, tds: 0, net: 0 }
    );
  }, [invoices, selectedIds]);

  const selectedBank = proposal?.bankAccounts.find((b) => String(b.id) === bankAccountId) || null;
  const bankLabel = selectedBank ? `${selectedBank.name} — ${selectedBank.bankName} (${selectedBank.accountNumber})` : '—';

  const canRun = selectedIds.size > 0 && !!bankAccountId && !!paymentDate;

  const handleRun = async () => {
    if (!canRun) return;
    setRunning(true);
    try {
      const res = await procurementService.createPaymentRun({
        invoiceIds: Array.from(selectedIds),
        paymentDate,
        bankAccountId,
        paymentMode,
      });
      setResult(res);
      setConfirmOpen(false);
      setToast({ message: `${res.totals.count} payment(s) created`, severity: 'success' });
      fetchProposal(horizonDays);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to create payment run', severity: 'error' });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Payment Proposal"
        subtitle="Pick which payables to release this cycle — cash-aware, TDS-netted, blocked invoices held out automatically"
        actions={
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={horizonDays}
              onChange={(_, v) => v !== null && setHorizonDays(v)}
            >
              <ToggleButton value={7}>7d</ToggleButton>
              <ToggleButton value={15}>15d</ToggleButton>
              <ToggleButton value={30}>30d</ToggleButton>
              <ToggleButton value={60}>60d</ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={() => fetchProposal(horizonDays)} disabled={loading}>
              <Refresh />
            </IconButton>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !proposal ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : proposal ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 2.5,
            }}
          >
            <StatCard title="Available Cash" value={formatIndianNumber(proposal.availableCash)} icon={<AccountBalanceWallet />} color={C.accent} />
            <StatCard title="Payable (Net)" value={formatIndianNumber(proposal.totals.net)} icon={<Payments />} color={C.purple} />
            <StatCard title="Invoices in Horizon" value={proposal.totals.count} icon={<ReceiptLong />} color={C.cyan} />
            <StatCard title="TDS to Deduct" value={formatIndianNumber(proposal.totals.tds)} icon={<MonetizationOn />} color={C.warn} />
          </Box>

          {result && <Box sx={{ mb: 2.5 }}><ResultPanel result={result} onDismiss={() => setResult(null)} /></Box>}

          <FilterBar>
            <TextField
              select
              size="small"
              label="Pay from account"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {proposal.bankAccounts.length === 0 && <MenuItem value="">No bank accounts available</MenuItem>}
              {proposal.bankAccounts.map((b) => (
                <MenuItem key={b.id} value={String(b.id)}>
                  {b.name} — {b.bankName} ({b.accountNumber}){b.isPrimary ? ' · Primary' : ''}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Mode"
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value as 'NEFT' | 'RTGS' | 'CHEQUE')}
              sx={{ minWidth: 140 }}
            >
              {PAYMENT_MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Payment date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 170 }}
            />

            <FilterSpacer />

            <Box sx={{ textAlign: 'right', mr: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {selectedIds.size} selected
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatIndianNumber(selectedTotals.net)}
              </Typography>
            </Box>

            <Tooltip title={!bankAccountId ? 'Select a bank account first' : selectedIds.size === 0 ? 'Select at least one invoice' : ''}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  disabled={!canRun}
                  onClick={() => setConfirmOpen(true)}
                >
                  Create Payment Run
                </Button>
              </span>
            </Tooltip>
          </FilterBar>

          <TableSurface>
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellSx, width: 44 }} padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleAll}
                        disabled={invoices.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={headerCellSx}>Vendor</TableCell>
                    <TableCell sx={headerCellSx}>Invoice #</TableCell>
                    <TableCell sx={headerCellSx}>Due Date</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                    <TableCell sx={headerCellSx}>Match</TableCell>
                    <TableCell sx={headerCellSx} align="right">Gross</TableCell>
                    <TableCell sx={headerCellSx} align="right">TDS</TableCell>
                    <TableCell sx={headerCellSx} align="right">Net</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ border: 0 }}>
                        <TableEmptyState
                          title="Nothing payable in this horizon"
                          hint="Try a longer horizon, or check the blocked list below."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((inv) => (
                      <TableRow
                        key={inv.invoiceId}
                        hover
                        selected={selectedIds.has(inv.invoiceId)}
                        onClick={() => toggleRow(inv.invoiceId)}
                        sx={{ ...bodyRowSx, cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox size="small" checked={selectedIds.has(inv.invoiceId)} onChange={() => toggleRow(inv.invoiceId)} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{inv.vendorName}</Typography>
                        </TableCell>
                        <TableCell>{inv.invoiceNumber}</TableCell>
                        <TableCell>{fmtDate(inv.dueDate)}</TableCell>
                        <TableCell>
                          <StatusDot label={dueLabel(inv.daysToDue)} tone={dueTone(inv.daysToDue)} />
                        </TableCell>
                        <TableCell>
                          <StatusDot label={inv.matchStatus.replace(/_/g, ' ')} tone={matchStatusTone[inv.matchStatus] || 'neutral'} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(inv.gross)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(inv.tds)}
                          {inv.tdsEstimated && (
                            <Tooltip title="Estimated — final TDS is fixed when the payment posts">
                              <Info sx={{ fontSize: 13, ml: 0.5, verticalAlign: 'middle', color: 'text.disabled' }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatIndianNumber(inv.net)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>

          <Box sx={{ mt: 2.5 }}>
            <Paper
              elevation={0}
              sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, cursor: 'pointer' }}
                onClick={() => setBlockedOpen((v) => !v)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Block sx={{ fontSize: 18, color: C.bad }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Blocked — held by exceptions ({proposal.blocked.length})
                  </Typography>
                </Box>
                <IconButton size="small">{blockedOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
              </Box>
              <Collapse in={blockedOpen}>
                <Divider />
                {proposal.blocked.length === 0 ? (
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="caption" color="text.secondary">Nothing is held back — every payable invoice is clean.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Vendor</TableCell>
                          <TableCell sx={headerCellSx}>Invoice #</TableCell>
                          <TableCell sx={headerCellSx}>Due Date</TableCell>
                          <TableCell sx={headerCellSx} align="right">Gross</TableCell>
                          <TableCell sx={headerCellSx}>Match</TableCell>
                          <TableCell sx={headerCellSx}>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {proposal.blocked.map((b) => (
                          <TableRow key={b.invoiceId} sx={bodyRowSx}>
                            <TableCell>{b.vendorName}</TableCell>
                            <TableCell>{b.invoiceNumber}</TableCell>
                            <TableCell>{fmtDate(b.dueDate)}</TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(b.gross)}</TableCell>
                            <TableCell><StatusDot label={b.matchStatus.replace(/_/g, ' ')} tone={matchStatusTone[b.matchStatus] || 'neutral'} /></TableCell>
                            <TableCell>
                              <StatusDot label={b.reason} tone="error" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Collapse>
            </Paper>
          </Box>
        </>
      ) : (
        <TableEmptyState title="Could not load the payment proposal" hint="Try refreshing." />
      )}

      <ConfirmRunDialog
        open={confirmOpen}
        count={selectedIds.size}
        net={selectedTotals.net}
        bankLabel={bankLabel}
        paymentMode={paymentMode}
        paymentDate={paymentDate}
        submitting={running}
        onConfirm={handleRun}
        onClose={() => setConfirmOpen(false)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default PaymentProposalPage;
